import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Check, X, MapPin, User, ShieldCheck, Gauge } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import InspectionReport from "./InspectionReport";
import AdminListingActions from "./admin/AdminListingActions";
import { resolveMediaUrl } from "../lib/fileUrl";
import { formatKmPolicyLine, hasKmPolicy } from "../lib/kmUnits";
import { toast } from "sonner";
import { formatLocalTimestamp } from "../lib/dateLocal";

function kmPolicyLabel(veh) {
  if (!veh || !hasKmPolicy(veh)) return "—";
  return formatKmPolicyLine(veh);
}

export default function AdminVehicleReview({ vehicleId, open, onClose, onApprove, onReject, onDelete, onPause, onResume }) {
  const [vehicle, setVehicle] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [host, setHost] = useState(null);
  const [notes, setNotes] = useState([]);
  const [baselineMeta, setBaselineMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [freshImagesRequired, setFreshImagesRequired] = useState(true);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!open || !vehicleId) {
      setVehicle(null);
      setBaseline(null);
      setHost(null);
      setNotes([]);
      setBaselineMeta(null);
      setRejectOpen(false);
      setRejectReason("");
      setFreshImagesRequired(true);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/admin/vehicles/${vehicleId}/review`,
          { withCredentials: true },
        );
        if (cancelled) return;
        setVehicle(data.vehicle);
        setBaseline(data.baseline);
        setHost(data.host);
        setNotes(data.notes || []);
        setBaselineMeta(data.baseline_meta || null);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.detail || "Could not load vehicle details");
          onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, vehicleId, onClose]);

  const handleApprove = async () => {
    setActing(true);
    try {
      await onApprove?.(vehicleId);
      onClose?.();
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    setActing(true);
    try {
      await onReject?.(vehicleId, rejectReason.trim() || undefined, freshImagesRequired);
      setRejectOpen(false);
      onClose?.();
    } finally {
      setActing(false);
    }
  };

  const photos = [
    ...(vehicle?.images || []).map((url) => resolveMediaUrl(url)),
    ...(baseline?.photos || []).map((p) => resolveMediaUrl(p.url || (p.file_id ? `${API}/files/${p.file_id}` : ""))),
  ].filter(Boolean);
  const uniquePhotos = [...new Set(photos)];

  const kycColor = (s) => {
    if (s === "approved") return "var(--zc-success)";
    if (s === "rejected" || s === "declined") return "var(--zc-danger,#dc2626)";
    if (s === "manual_review") return "var(--zc-warn,#f59e0b)";
    return "var(--zc-text-3)";
  };

  return (
    <>
      <Dialog open={open && !rejectOpen} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent
          className="bg-white border border-[var(--zc-border)] max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="admin-vehicle-review"
        >
          <DialogHeader>
            <DialogTitle className="zc-headline text-2xl">
              {loading ? "Loading…" : vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : "Vehicle review"}
            </DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="py-12 text-center text-sm text-[var(--zc-text-3)]">Fetching listing profile…</div>
          )}

          {!loading && vehicle && (
            <div className="space-y-6 mt-2">
              {baselineMeta?.stale && (
                <div className="zc-card p-4 border border-[var(--zc-warn,#f59e0b)]/50 bg-[var(--zc-warn,#f59e0b)]/10" data-testid="admin-baseline-stale-banner">
                  <div className="font-semibold text-sm text-[var(--zc-text)]">Fresh AI baseline required</div>
                  <p className="text-sm text-[var(--zc-text-2)] mt-1">
                    Listing details changed since the last scan. The host must complete a new 6-angle condition scan before you approve.
                  </p>
                </div>
              )}

              {baseline && baselineMeta && !baselineMeta.matches_current_listing && !baselineMeta.stale && (
                <div className="zc-card p-4 border border-[var(--zc-warn,#f59e0b)]/50" data-testid="admin-baseline-outdated-banner">
                  <div className="font-semibold text-sm text-[var(--zc-text)]">Baseline may be outdated</div>
                  <p className="text-sm text-[var(--zc-text-2)] mt-1">
                    Colors or plate on file differ from the stored baseline snapshot. Confirm the host rescanned after their edits.
                  </p>
                </div>
              )}

              {vehicle.pending_changes?.fields?.length > 0 && (
                <div className="zc-card p-4 border border-[var(--zc-warn,#f59e0b)]/40" data-testid="admin-vehicle-edit-diff">
                  <div className="eyebrow mb-2">Host edit — amended fields</div>
                  <ul className="space-y-2 text-sm">
                    {vehicle.pending_changes.fields.map((c) => (
                      <li key={c.field} className="flex flex-wrap gap-x-2 text-[var(--zc-text-2)]">
                        <span className="font-semibold text-[var(--zc-text)]">{c.label}:</span>
                        <span className="line-through opacity-70">{c.before}</span>
                        <span>→</span>
                        <span className="text-[var(--zc-accent)] font-semibold">{c.after}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {notes.length > 0 && (
                <div className="zc-card p-4" data-testid="admin-vehicle-notes">
                  <div className="eyebrow mb-2">Notes</div>
                  <ul className="space-y-2 text-sm text-[var(--zc-text-2)]">
                    {notes.map((n) => (
                      <li key={n.note_id}>
                        <div>{n.body}</div>
                        <div className="text-caption text-[var(--zc-text-3)] mt-0.5">{formatLocalTimestamp(n.created_at)}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {uniquePhotos.length > 0 && (
                <div>
                  <div className="eyebrow mb-2">Photos ({uniquePhotos.length})</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {uniquePhotos.map((src, i) => (
                      <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="block aspect-[4/3] rounded-lg overflow-hidden bg-[var(--zc-bg-3)]">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {baseline ? (
                <div>
                  <div className="eyebrow mb-2">AI condition · listing baseline</div>
                  <InspectionReport
                    inspection={baseline}
                    listedExteriorColor={baselineMeta?.listed_exterior_color || vehicle.exterior_color}
                    listedInteriorColor={baselineMeta?.listed_interior_color || vehicle.interior_color}
                  />
                </div>
              ) : (
                <div className="text-sm text-[var(--zc-text-3)] p-3 rounded-lg bg-[var(--zc-bg-2)] border border-[var(--zc-border)]">
                  No baseline inspection on file.
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="zc-card p-4">
                  <div className="eyebrow mb-2">Registration</div>
                  <div className="font-mono text-xs break-all">{vehicle.vin || "—"}</div>
                  <div className="text-xs text-[var(--zc-text-3)] mt-1">VIN</div>
                  <div className="font-mono text-xs mt-3">{vehicle.registration_number || "—"}</div>
                  <div className="text-xs text-[var(--zc-text-3)] mt-1">Plate</div>
                </div>
                <div className="zc-card p-4">
                  <div className="eyebrow mb-2 flex items-center gap-1.5"><Gauge size={12} /> Mileage policy</div>
                  <div className="text-[var(--zc-text-2)]">{kmPolicyLabel(vehicle)}</div>
                  {vehicle.allows_one_way && (
                    <div className="text-xs text-[var(--zc-text-3)] mt-2">One-way allowed · ${vehicle.one_way_fee} fee</div>
                  )}
                </div>
              </div>

              <div className="zc-card p-4">
                <div className="eyebrow mb-2 flex items-center gap-1.5"><User size={12} /> Host</div>
                <div className="text-sm font-semibold">{vehicle.owner_name || host?.name || "—"}</div>
                {host?.email && <div className="text-xs text-[var(--zc-text-3)] mt-0.5">{host.email}</div>}
                <div className="mt-2 flex items-center gap-1.5 text-xs">
                  <ShieldCheck size={12} style={{ color: kycColor(host?.kyc_status) }} />
                  <span style={{ color: kycColor(host?.kyc_status) }} className="capitalize font-semibold">
                    KYC {(host?.kyc_status || "unknown").replace(/_/g, " ")}
                  </span>
                </div>
              </div>

              <div className="zc-card p-4">
                <div className="eyebrow mb-2 flex items-center gap-1.5"><MapPin size={12} /> Location</div>
                <div className="text-sm">{vehicle.location_address || "—"}</div>
                <div className="text-xs text-[var(--zc-text-3)] mt-1">{vehicle.location_city}</div>
              </div>

              {vehicle.description && (
                <div>
                  <div className="eyebrow mb-2">Description</div>
                  <p className="text-sm text-[var(--zc-text-2)] leading-relaxed">{vehicle.description}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--zc-border)]">
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={acting}
                  className="px-4 h-10 zc-btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
                  data-testid={`review-approve-${vehicleId}`}
                >
                  <Check size={14} /> Approve
                </button>
                <button
                  type="button"
                  onClick={() => setRejectOpen(true)}
                  disabled={acting}
                  className="px-4 h-10 zc-btn-ghost text-sm flex items-center gap-1.5 disabled:opacity-50"
                  data-testid={`review-reject-${vehicleId}`}
                >
                  <X size={14} /> Reject
                </button>
                <AdminListingActions
                  item={{ ...vehicle, type: "vehicle", listing_id: vehicle.vehicle_id }}
                  onPause={onPause}
                  onResume={onResume}
                  onDelete={onDelete ? () => onDelete(vehicle) : undefined}
                  size="lg"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={(o) => !o && setRejectOpen(false)}>
        <DialogContent className="bg-white border border-[var(--zc-border)] max-w-md" data-testid="admin-vehicle-reject-reason">
          <DialogHeader>
            <DialogTitle className="zc-headline text-xl">Reject listing</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--zc-text-2)]">
            Tell the host what to correct, then choose whether new condition images are required.
          </p>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Photos don't match registration, incomplete baseline scan…"
            className="zc-input w-full min-h-[88px] p-3 text-sm mt-2"
            data-testid="reject-reason-input"
          />
          <label htmlFor={`fresh-ai-images-${vehicleId}`} className="mt-4 flex items-start gap-3 text-sm text-[var(--zc-text)]">
            <input
              id={`fresh-ai-images-${vehicleId}`}
              type="checkbox"
              checked={freshImagesRequired}
              onChange={(e) => setFreshImagesRequired(e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-[var(--zc-accent)]"
              data-testid="reject-fresh-ai-images"
            />
            <span>
              <span className="font-semibold block">Require fresh AI condition images</span>
              <span className="text-[var(--zc-text-2)]">
                Keep checked when the current evidence is unclear, incomplete, or suspect. If unchecked,
                existing evidence is still reassessed after material identity edits.
              </span>
            </span>
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={() => setRejectOpen(false)} className="px-4 h-10 zc-btn-ghost text-sm">Cancel</button>
            <button
              type="button"
              onClick={handleReject}
              disabled={acting}
              className="px-4 h-10 text-sm rounded-md text-white disabled:opacity-50"
              style={{ background: "var(--zc-danger,#dc2626)" }}
              data-testid="reject-reason-submit"
            >
              {acting ? "Rejecting…" : "Reject listing"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
